import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { MailTemplateVars } from '@adornis/bh-matching-mails/entities/mail-templates.js';
import { MatchingLanguage } from '@adornis/bh-matching-user/entities/auto-matching-fields.js';
import {
  BuddyUserMentee,
  BuddyUserMentor,
  Gender,
  GenderPreference,
} from '@adornis/bh-matching-user/entities/buddy-user.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { validate } from '@adornis/validation/decorators.js';
import { and } from '@adornis/validation/functions/and.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { intervalToDuration } from 'date-fns';

/**
 * ANLEITUNG:
 * @Markus
 * - enums müssen immer den Namen und Identifier haben
 * - enums müssen registriert werden  registerEnum({ enum: ConfirmEnum, name: 'ConfirmEnum' });
 * - Felder hinzufügen, löschen erstmal möglich
 * - Validation löschen ist kein Problem
 * ! Validation hinzufügen, muss eine Migration vorgenommen werden
 *
 * 1. Felder anlegen
 * 2. Translation in workspaces/mentoring-uni-frankfurt/src/translations.ts einfügen
 * 3. Felder müssen in workspaces/mentoring-uni-frankfurt/src/client/mentoring-ffm-signup.ts (Mentee) bzw. workspaces/mentoring-uni-frankfurt/src/client/pages/mentoring-mentor-sigup.ts (Mentor) hinzugefügt werden, damit es dann an der richtigen Stelle erscheint
 *
 */

export enum PreferredLanguage {
  GERMAN = 'GERMAN',
  GERMAN_ENGLISH = 'GERMAN_ENGLISH',
  ENGLISH = 'ENGLISH',
}

/**
 * Available dates for the UNI:hautnah Matching Days 2026
 */
export enum MatchingDayDate {
  MONDAY_MAY_11 = 'MONDAY_MAY_11',
  TUESDAY_MAY_12 = 'TUESDAY_MAY_12',
  WEDNESDAY_MAY_13 = 'WEDNESDAY_MAY_13',
}

/**
 * Study programs available for UNI:hautnah mentors
 */
export enum StudyProgram {
  STUDY_AFRICAN_LANGUAGES = 'STUDY_AFRICAN_LANGUAGES',
  STUDY_COMPARATIVE_LITERATURE = 'STUDY_COMPARATIVE_LITERATURE',
  STUDY_AMERICAN_STUDIES = 'STUDY_AMERICAN_STUDIES',
  STUDY_ARCHAEOLOGICAL_SCIENCES = 'STUDY_ARCHAEOLOGICAL_SCIENCES',
  STUDY_BIOCHEMISTRY = 'STUDY_BIOCHEMISTRY',
  STUDY_BIOINFORMATICS = 'STUDY_BIOINFORMATICS',
  STUDY_BIOPHYSICS = 'STUDY_BIOPHYSICS',
  STUDY_BIOSCIENCES = 'STUDY_BIOSCIENCES',
  STUDY_CHEMISTRY = 'STUDY_CHEMISTRY',
  STUDY_EMPIRICAL_LINGUISTICS = 'STUDY_EMPIRICAL_LINGUISTICS',
  STUDY_ENGLISH_STUDIES = 'STUDY_ENGLISH_STUDIES',
  STUDY_EDUCATIONAL_SCIENCES = 'STUDY_EDUCATIONAL_SCIENCES',
  STUDY_ETHNOLOGY = 'STUDY_ETHNOLOGY',
  STUDY_PROTESTANT_THEOLOGY = 'STUDY_PROTESTANT_THEOLOGY',
  STUDY_GEOGRAPHY = 'STUDY_GEOGRAPHY',
  STUDY_GEOSCIENCES = 'STUDY_GEOSCIENCES',
  STUDY_GERMAN_STUDIES = 'STUDY_GERMAN_STUDIES',
  STUDY_HISTORY = 'STUDY_HISTORY',
  STUDY_GREEK_PHILOLOGY = 'STUDY_GREEK_PHILOLOGY',
  STUDY_COMPUTER_SCIENCE = 'STUDY_COMPUTER_SCIENCE',
  STUDY_ISLAMIC_STUDIES = 'STUDY_ISLAMIC_STUDIES',
  STUDY_JAPANOLOGY = 'STUDY_JAPANOLOGY',
  STUDY_JUDAIC_STUDIES = 'STUDY_JUDAIC_STUDIES',
  STUDY_CATHOLIC_THEOLOGY = 'STUDY_CATHOLIC_THEOLOGY',
  STUDY_ART_HISTORY = 'STUDY_ART_HISTORY',
  STUDY_KOREASTUDIES = 'STUDY_KOREASTUDIES',
  STUDY_CULTURAL_ANTHROPOLOGY = 'STUDY_CULTURAL_ANTHROPOLOGY',
  STUDY_LATIN_PHILOLOGY = 'STUDY_LATIN_PHILOLOGY',
  STUDY_SPECIAL_NEEDS_EDUCATION = 'STUDY_SPECIAL_NEEDS_EDUCATION',
  STUDY_PRIMARY_SCHOOL_TEACHING = 'STUDY_PRIMARY_SCHOOL_TEACHING',
  STUDY_SECONDARY_SCHOOL_TEACHING = 'STUDY_SECONDARY_SCHOOL_TEACHING',
  STUDY_MIDDLE_SCHOOL_TEACHING = 'STUDY_MIDDLE_SCHOOL_TEACHING',
  STUDY_LINGUISTICS = 'STUDY_LINGUISTICS',
  STUDY_MATHEMATICS = 'STUDY_MATHEMATICS',
  STUDY_MEDICINE = 'STUDY_MEDICINE',
  STUDY_MEDICAL_TECHNOLOGY = 'STUDY_MEDICAL_TECHNOLOGY',
  STUDY_METEOROLOGY = 'STUDY_METEOROLOGY',
  STUDY_MUSICOLOGY = 'STUDY_MUSICOLOGY',
  STUDY_PHARMACY = 'STUDY_PHARMACY',
  STUDY_PHILOSOPHY = 'STUDY_PHILOSOPHY',
  STUDY_PHYSICS = 'STUDY_PHYSICS',
  STUDY_POLITICAL_SCIENCE = 'STUDY_POLITICAL_SCIENCE',
  STUDY_PSYCHOLOGY = 'STUDY_PSYCHOLOGY',
  STUDY_LAW = 'STUDY_LAW',
  STUDY_RELIGIOUS_STUDIES = 'STUDY_RELIGIOUS_STUDIES',
  STUDY_ROMANCE_STUDIES = 'STUDY_ROMANCE_STUDIES',
  STUDY_SINOLOGY = 'STUDY_SINOLOGY',
  STUDY_SCANDINAVIAN_STUDIES = 'STUDY_SCANDINAVIAN_STUDIES',
  STUDY_SOCIOLOGY = 'STUDY_SOCIOLOGY',
  STUDY_SPORTS_SCIENCE = 'STUDY_SPORTS_SCIENCE',
  STUDY_SOUTHEAST_ASIAN_STUDIES = 'STUDY_SOUTHEAST_ASIAN_STUDIES',
  STUDY_THEATRE_FILM_MEDIA = 'STUDY_THEATRE_FILM_MEDIA',
  STUDY_ECONOMICS_EDUCATION = 'STUDY_ECONOMICS_EDUCATION',
  STUDY_ECONOMICS = 'STUDY_ECONOMICS',
  STUDY_DENTISTRY = 'STUDY_DENTISTRY',
}

export enum SchoolName {
  Adolf_Reichwein_Schule = 'Adolf_Reichwein_Schule',
  Adorno_Gymnasium = 'Adorno_Gymnasium',
  Albert_Einstein_Gymnasium = 'Albert_Einstein_Gymnasium',
  Albert_Einstein_Schule = 'Albert_Einstein_Schule',
  Albert_Schweitzer_Schule = 'Albert_Schweitzer_Schule',
  Altkonigschule = 'Altkonigschule',
  Anna_Schmidt_Schule = 'Anna_Schmidt_Schule',
  Begemann_Schule = 'Begemann_Schule',
  Bertha_von_Suttner_Schule = 'Bertha_von_Suttner_Schule',
  Berufliche_Schulen_Gross_Gerau = 'Berufliche_Schulen_Gross_Gerau',
  Berufliches_Gymnasium_des_Main_Kinzig_Kreises = 'Berufliches_Gymnasium_des_Main_Kinzig_Kreises',
  Bischof_Neumann_Schule = 'Bischof_Neumann_Schule',
  Carl_von_Weinberg_Schule = 'Carl_von_Weinberg_Schule',
  Claus_von_Stauffenberg_Schule = 'Claus_von_Stauffenberg_Schule',
  Dreieichschule = 'Dreieichschule',
  Eduard_Stiehler_Schule = 'Eduard_Stiehler_Schule',
  Eichendorffschule = 'Eichendorffschule',
  Einhardschule = 'Einhardschule',
  Elisabethenschule = 'Elisabethenschule',
  Ernst_Gobel_Schule = 'Ernst_Gobel_Schule',
  Ernst_Reuter_Schule = 'Ernst_Reuter_Schule',
  Fanny_Leicht_Gymnasium = 'Fanny_Leicht_Gymnasium',
  Feldbergschule = 'Feldbergschule',
  Ferdinand_Braun_Schule = 'Ferdinand_Braun_Schule',
  Franziskanergymnasium = 'Franziskanergymnasium',
  Freiherr_vom_Stein_Schule_Frankfurt = 'Freiherr_vom_Stein_Schule_Frankfurt',
  Freiherr_vom_Stein_Schule_Fulda = 'Freiherr_vom_Stein_Schule_Fulda',
  Friedrich_Dessauer_Gymnasium_Frankfurt = 'Friedrich_Dessauer_Gymnasium_Frankfurt',
  Friedrich_Dessauer_Gymnasium_Aschaffenburg = 'Friedrich_Dessauer_Gymnasium_Aschaffenburg',
  Georg_Ackermann_Schule = 'Georg_Ackermann_Schule',
  Georg_Buchner_Gymnasium = 'Georg_Buchner_Gymnasium',
  Gesamtschule_Gedern = 'Gesamtschule_Gedern',
  Gesamtschule_Konradsdorf = 'Gesamtschule_Konradsdorf',
  Goethe_Gymnasium = 'Goethe_Gymnasium',
  Goetheschule = 'Goetheschule',
  Graf_Stauffenberg_Gymnasium = 'Graf_Stauffenberg_Gymnasium',
  Grimmelshausen_Gymnasium = 'Grimmelshausen_Gymnasium',
  Gymnasium_Oberursel = 'Gymnasium_Oberursel',
  Gymnasium_Riedberg = 'Gymnasium_Riedberg',
  Hanns_Seidel_Gymnasium = 'Hanns_Seidel_Gymnasium',
  Heinrich_Boll_Schule = 'Heinrich_Boll_Schule',
  Heinrich_Mann_Schule = 'Heinrich_Mann_Schule',
  Heinrich_von_Gagern_Gymnasium = 'Heinrich_von_Gagern_Gymnasium',
  Heinrich_von_Kleist_Schule = 'Heinrich_von_Kleist_Schule',
  Helmholtzschule = 'Helmholtzschule',
  Henry_Harnischfeger_Schule = 'Henry_Harnischfeger_Schule',
  Hermann_Staudinger_Gymnasium = 'Hermann_Staudinger_Gymnasium',
  Hochtaunusschule = 'Hochtaunusschule',
  Hohe_Landesschule_Hanau = 'Hohe_Landesschule_Hanau',
  Humboldtschule = 'Humboldtschule',
  Immanuel_Kant_Schule = 'Immanuel_Kant_Schule',
  IGS_Stierstadt = 'IGS_Stierstadt',
  Internatsschule_Schloss_Hansenberg = 'Internatsschule_Schloss_Hansenberg',
  Johannes_Butzbach_Gymnasium = 'Johannes_Butzbach_Gymnasium',
  Julius_Echter_Gymnasium = 'Julius_Echter_Gymnasium',
  Kaiserin_Friedrich_Gymnasium = 'Kaiserin_Friedrich_Gymnasium',
  Karl_Rehbein_Schule = 'Karl_Rehbein_Schule',
  Karl_Theodor_von_Dalberg_Gymnasium = 'Karl_Theodor_von_Dalberg_Gymnasium',
  Kinzig_Schule = 'Kinzig_Schule',
  Konrad_Zuse_Schule = 'Konrad_Zuse_Schule',
  Kopernikusschule = 'Kopernikusschule',
  Kronberg_Gymnasium = 'Kronberg_Gymnasium',
  Landgraf_Ludwig_Gymnasium = 'Landgraf_Ludwig_Gymnasium',
  Lessing_Gymnasium = 'Lessing_Gymnasium',
  Liebigschule_Frankfurt = 'Liebigschule_Frankfurt',
  Liebigschule_Giessen = 'Liebigschule_Giessen',
  Limesschule = 'Limesschule',
  Main_Taunus_Schule = 'Main_Taunus_Schule',
  Maria_Ward_Schule_Aschaffenburg = 'Maria_Ward_Schule_Aschaffenburg',
  Maria_Ward_Schule_BadHomburg = 'Maria_Ward_Schule_BadHomburg',
  Marianum = 'Marianum',
  Marienschule_Offenbach = 'Marienschule_Offenbach',
  Marienschule_Fulda = 'Marienschule_Fulda',
  Max_Beckmann_Schule = 'Max_Beckmann_Schule',
  Max_Eyth_Schule = 'Max_Eyth_Schule',
  Max_Planck_Schule = 'Max_Planck_Schule',
  Musterschule = 'Musterschule',
  Neues_Gymnasium = 'Neues_Gymnasium',
  Otto_Hahn_Schule_Frankfurt = 'Otto_Hahn_Schule_Frankfurt',
  Otto_Hahn_Schule_Hanau = 'Otto_Hahn_Schule_Hanau',
  Paul_Gerhardt_Schule = 'Paul_Gerhardt_Schule',
  Philipp_Reis_Schule = 'Philipp_Reis_Schule',
  Pralat_Diehl_Schule = 'Pralat_Diehl_Schule',
  Rabanus_Maurus_Schule = 'Rabanus_Maurus_Schule',
  Ricarda_Huch_Schule = 'Ricarda_Huch_Schule',
  Richard_Muller_Schule = 'Richard_Muller_Schule',
  Robert_Koch_Schule_Frankfurt = 'Robert_Koch_Schule_Frankfurt',
  Rudolf_Koch_Schule = 'Rudolf_Koch_Schule',
  St_Angela_Schule = 'St_Angela_Schule',
  Schillerschule = 'Schillerschule',
  Schule_am_Ried = 'Schule_am_Ried',
  Schule_und_Internat_Steinmuhle = 'Schule_und_Internat_Steinmuhle',
  Singbergschule = 'Singbergschule',
  Strothoff_International_School = 'Strothoff_International_School',
  Taunusgymnasium = 'Taunusgymnasium',
  Ulrich_von_Hutten_Gymnasium = 'Ulrich_von_Hutten_Gymnasium',
  Waldorfschule = 'Waldorfschule',
  Weibelfeldschule = 'Weibelfeldschule',
  Wigbertschule = 'Wigbertschule',
  Winfriedschule = 'Winfriedschule',
  Wohlerschule = 'Wohlerschule',
  Wolfgang_Ernst_Gymnasium = 'Wolfgang_Ernst_Gymnasium',
  Ziehenschule = 'Ziehenschule',
}

/**
 * Teaching subjects for UNI:hautnah mentors who study teaching
 */
export enum TeachingSubject {
  SUBJECT_BIOLOGY = 'SUBJECT_BIOLOGY',
  SUBJECT_CHEMISTRY = 'SUBJECT_CHEMISTRY',
  SUBJECT_GERMAN = 'SUBJECT_GERMAN',
  SUBJECT_ENGLISH = 'SUBJECT_ENGLISH',
  SUBJECT_GEOGRAPHY = 'SUBJECT_GEOGRAPHY',
  SUBJECT_PROTESTANT_RELIGION = 'SUBJECT_PROTESTANT_RELIGION',
  SUBJECT_FRENCH = 'SUBJECT_FRENCH',
  SUBJECT_HISTORY = 'SUBJECT_HISTORY',
  SUBJECT_GREEK = 'SUBJECT_GREEK',
  SUBJECT_COMPUTER_SCIENCE = 'SUBJECT_COMPUTER_SCIENCE',
  SUBJECT_ISLAMIC_RELIGION = 'SUBJECT_ISLAMIC_RELIGION',
  SUBJECT_ITALIAN = 'SUBJECT_ITALIAN',
  SUBJECT_CATHOLIC_RELIGION = 'SUBJECT_CATHOLIC_RELIGION',
  SUBJECT_ART = 'SUBJECT_ART',
  SUBJECT_LATIN = 'SUBJECT_LATIN',
  SUBJECT_MATHEMATICS = 'SUBJECT_MATHEMATICS',
  SUBJECT_MUSIC = 'SUBJECT_MUSIC',
  SUBJECT_PHILOSOPHY = 'SUBJECT_PHILOSOPHY',
  SUBJECT_PHYSICS = 'SUBJECT_PHYSICS',
  SUBJECT_POLITICS_ECONOMICS = 'SUBJECT_POLITICS_ECONOMICS',
  SUBJECT_SPANISH = 'SUBJECT_SPANISH',
  SUBJECT_SPORTS = 'SUBJECT_SPORTS',
}

/**
 * Grade levels for UNI:hautnah student mentees
 */
export enum GradeLevel {
  Q4 = 'Q4',
  Q2 = 'Q2',
  E2 = 'E2',
  GRADE_10_OR_LOWER = 'GRADE_10_OR_LOWER',
  GRADUATED = 'GRADUATED',
}

export const GenderTitles = {
  [Gender.MALE]: 'Herr',
  [Gender.FEMALE]: 'Frau',
};

registerEnum({ enum: PreferredLanguage, name: 'PreferredLanguage' });
registerEnum({ enum: MatchingDayDate, name: 'MatchingDayDate' });
registerEnum({ enum: StudyProgram, name: 'StudyProgram' });
registerEnum({ enum: SchoolName, name: 'SchoolName' });
registerEnum({ enum: TeachingSubject, name: 'TeachingSubject' });
registerEnum({ enum: GradeLevel, name: 'GradeLevel' });

@Entity()
export class MatchingHautnahMentorUser extends BuddyUserMentor {
  static override _class = 'MatchingHautnahMentorUser';

  constructor(data: PartialEntityData<MatchingHautnahMentorUser>) {
    super(data);
  }

  static override emailValidator: ({
    value,
    key,
    target,
  }: import('@adornis/validation/validator.js').ValidationFunctionOptions<any, any>) => void = and(
    super.emailValidator,
    ({ value, key, target }) => {},
  );

  /**
   * First name of the mentor
   */
  @validate(nonOptional())
  @Field(() => String)
  firstName: Maybe<string>;

  /**
   * Last name of the mentor
   */
  @validate(nonOptional())
  @Field(type => String)
  lastName: Maybe<string>;

  @validate(nonOptional())
  @Field(() => Date)
  dateOfBirth: Maybe<Date>;

  /**
   * Optional phone number for faster contact
   */
  @Field(type => String)
  phoneNumber: Maybe<string>;

  /**
   * Available dates for the matching days
   */
  @validate(nonOptional())
  @Field(type => [MatchingDayDate])
  availableDates: Maybe<MatchingDayDate[]>;

  /**
   * Number of mentees the mentor can take (1-3)
   */
  @validate(nonOptional())
  @Field(() => Int)
  numberOfMentees: Maybe<number>;

  /**
   * Study programs the mentor can show to mentees
   */
  @validate(nonOptional())
  @Field(type => [StudyProgram])
  studyPrograms: Maybe<StudyProgram[]>;

  /**
   * Teaching subjects if the mentor studies teaching
   */
  @Field(type => [TeachingSubject])
  teachingSubjects: Maybe<TeachingSubject[]>;

  /**
   * Additional notes or comments
   */
  @Field(type => String)
  additionalNotes: Maybe<string>;

  /**
   * Data privacy consent
   */
  @validate(nonOptional())
  @Field(type => Boolean)
  dataPrivacyConsent: Maybe<boolean>;

  /**
   * Gender of the mentor
   */
  @validate(nonOptional())
  @Field(() => Gender)
  gender: Maybe<Gender>;

  /**
   * Gender preference for matching
   */
  @Field(() => GenderPreference)
  genderPreference: Maybe<GenderPreference>;

  /**
   * Native language of the mentor
   */
  @validate(nonOptional())
  @Field(() => MatchingLanguage)
  nativeLanguage: Maybe<MatchingLanguage>;

  /**
   * Foreign languages spoken by the mentor
   */
  @Field(() => [MatchingLanguage])
  foreignLanguages: Maybe<MatchingLanguage[]>;

  /**
   * Preferred language for communication
   */
  @validate(nonOptional())
  @Field(() => PreferredLanguage)
  wunschsprache: Maybe<PreferredLanguage>;

  @Field(() => Int, {
    resolve: function (this: MatchingHautnahMenteeUser) {
      return () => {
        if (this.dateOfBirth) {
          const today = new Date();
          const age = intervalToDuration({ start: this.dateOfBirth, end: today }).years ?? null;
          return age;
        }
        return undefined;
      };
    },
  })
  age: Maybe<number>;

  static override getMailVariables(user: MatchingHautnahMentorUser): [string, string][] {
    logger.info({ user }, 'user vars in getMailVariables 1');
    const salutation: [string, string] = [MailTemplateVars.SALUTATION, GenderTitles[user.gender ?? ''] ?? ''];

    // Convert study programs to readable format
    const studyProgramsText = user.studyPrograms?.map(program => StudyProgram[program]).join(', ');

    // Convert available dates to readable format
    const availableDatesText = user.availableDates
      ?.map(date => {
        switch (date) {
          case MatchingDayDate.MONDAY_MAY_11:
            return 'Montag, 11.05.2026';
          case MatchingDayDate.TUESDAY_MAY_12:
            return 'Dienstag, 12.05.2026';
          case MatchingDayDate.WEDNESDAY_MAY_13:
            return 'Mittwoch, 13.05.2026';
          default:
            return '';
        }
      })
      .filter(date => date)
      .join(', ');

    return [
      ...super.getMailVariables(user),
      ...Object.entries(user.toObject()).map(([k, v]) => [k, v?.toString() ?? ''] as const),
      salutation,
      ['availableDates', availableDatesText || ''],
      ['numberOfMentees', String(user.numberOfMentees || 1)],
      ['studyPrograms', studyProgramsText || ''],
    ].map(([k, v]) => [k!, v ?? ''] as const);
  }
}

@Entity()
export class MatchingHautnahMenteeUser extends BuddyUserMentee {
  static override _class = 'MatchingHautnahMenteeUser';

  constructor(data: PartialEntityData<MatchingHautnahMenteeUser>) {
    super(data);
  }

  /**
   * First name of the mentee
   */
  @validate(nonOptional())
  @Field(() => String)
  firstName: Maybe<string>;

  /**
   * Last name of the mentee
   */
  @validate(nonOptional())
  @Field(type => String)
  lastName: Maybe<string>;

  // Email is already defined in the base class

  @validate(nonOptional())
  @Field(() => Date)
  dateOfBirth: Maybe<Date>;

  /**
   * Optional phone number for faster contact
   */
  @Field(type => String)
  phoneNumber: Maybe<string>;

  /**
   * Grade level of the mentee
   */
  @validate(nonOptional())
  @Field(() => GradeLevel)
  gradeLevel: Maybe<GradeLevel>;

  /**
   * School name (selected from dropdown)
   */
  @Field(() => SchoolName)
  schoolName: Maybe<SchoolName>;

  /**
   * Custom school name (if not in dropdown)
   */
  @Field(() => String)
  customSchoolName: Maybe<string>;

  /**
   * Available dates for the matching days
   */
  @validate(nonOptional())
  @Field(type => [MatchingDayDate])
  availableDates: Maybe<MatchingDayDate[]>;

  /**
   * Primary study program of interest
   */
  @validate(nonOptional())
  @Field(type => StudyProgram)
  primaryStudyInterest: Maybe<StudyProgram>;

  /**
   * Additional study programs of interest
   */
  @Field(type => [StudyProgram])
  additionalStudyInterests: Maybe<StudyProgram[]>;

  /**
   * Teaching subject of interest (if interested in Lehramt)
   */
  @Field(type => TeachingSubject)
  teachingSubjectInterest: Maybe<TeachingSubject>;

  /**
   * Confirmation of single registration
   */
  @validate(nonOptional())
  @Field(type => Boolean)
  singleRegistrationConfirmed: Maybe<boolean>;

  /**
   * Data privacy consent
   */
  @validate(nonOptional())
  @Field(type => Boolean)
  dataPrivacyConsent: Maybe<boolean>;

  /**
   * Gender of the mentee
   */
  @validate(nonOptional())
  @Field(() => Gender)
  gender: Maybe<Gender>;

  /**
   * Gender preference for matching
   */
  @Field(() => GenderPreference)
  genderPreference: Maybe<GenderPreference>;

  /**
   * Number of mentees the mentee want to be tought with (1-2 / 3-4)
   */
  @validate(nonOptional())
  @Field(() => Int)
  numberOfFellowMentees: Maybe<number>;

  @Field(() => String)
  parentalConsentDocID: Maybe<String>;

  @DataLoadedField(type => AdornisFile, ({ parentalConsentDocID }) => parentalConsentDocID)
  parentalConsentDoc: Maybe<AdornisFile>;

  /**
   * Additional notes or comments
   */
  @Field(type => String)
  additionalNotes: Maybe<string>;

  @Field(() => Int, {
    resolve: function (this: MatchingHautnahMenteeUser) {
      return () => {
        if (this.dateOfBirth) {
          const today = new Date();
          const age = intervalToDuration({ start: this.dateOfBirth, end: today }).years ?? null;
          return age;
        }
        return undefined;
      };
    },
  })
  age: Maybe<number>;

  static override getMailVariables(user: MatchingHautnahMenteeUser): [string, string][] {
    const salutation: [string, string] = [MailTemplateVars.SALUTATION, GenderTitles[user.gender ?? ''] ?? ''];

    // Convert available dates to readable format
    const availableDatesText = user.availableDates
      ?.map(date => {
        switch (date) {
          case MatchingDayDate.MONDAY_MAY_11:
            return 'Montag, 11.05.2026';
          case MatchingDayDate.TUESDAY_MAY_12:
            return 'Dienstag, 12.05.2026';
          case MatchingDayDate.WEDNESDAY_MAY_13:
            return 'Mittwoch, 13.05.2026';
          default:
            return '';
        }
      })
      .filter(date => date)
      .join(', ');

    // Get school name (either from dropdown or custom entry)
    const schoolNameText = user.customSchoolName || user.schoolName || '';

    // Convert primary study program to text
    const studyProgramText = user.primaryStudyInterest ? StudyProgram[user.primaryStudyInterest] : '';

    return [
      ...super.getMailVariables(user),
      salutation,
      ['availableDates', availableDatesText || ''],
      ['schoolName', schoolNameText],
      ['studyProgram', studyProgramText],
    ];
  }
}
